/**
 * KI-Spur — alle Vorschläge von Clara und dem Belegleser, und was daraus wurde
 * (Etappe 3, 28.09.2026). Einstieg über Einstellungen.
 *
 * Oben: meldet sich der Evo, ist ein Schreibtisch in Paperclip angehalten.
 * Dann die Quoten der letzten 90 Tage je Art — die Messlatte, ob ein Text
 * wirklich taugt (übernommen, geändert, verworfen, nie angesehen). Darunter die
 * Liste, filterbar; ein Tipp klappt die Herkunft auf.
 */
import { fehlerText } from "@vimaana/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { KiHerkunftInhalt } from "../components/ki/KiHerkunft";
import { KI_ART, KI_EREIGNIS, kiDauer, useKiListe, useKiQuoten, useKiStand, type KiListenEintrag } from "../hooks/useKiSpur";
import "../styles/projekte.css";
import "../styles/post.css";
import "../styles/ki-spur.css";

const ARTEN = [
  { id: "", label: "Alle" },
  { id: "clara_antwort", label: "Mailentwurf" },
  { id: "beleg_text", label: "Beleg (Text)" },
  { id: "beleg_bild", label: "Beleg (Bild)" },
];
const ENTSCHEIDUNGEN = [
  { id: "", label: "jede" },
  { id: "offen", label: "noch offen" },
  { id: "uebernommen", label: "übernommen" },
  { id: "geaendert", label: "geändert" },
  { id: "verworfen", label: "verworfen" },
  { id: "gesendet", label: "gesendet" },
];

function zeit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function prozent(teil: number, ganz: number): string {
  return ganz ? `${Math.round((teil / ganz) * 100)} %` : "–";
}

function Zeile({ z }: { z: KiListenEintrag }) {
  const [auf, setAuf] = useState(false);
  const entscheidungen = [...new Set(z.ereignisse.filter((e) => e.art !== "gezeigt").map((e) => e.art))];
  return (
    <li className={"ki-zeile" + (auf ? " ist-auf" : "")}>
      <button type="button" className="ki-zeile-kopf" aria-expanded={auf} onClick={() => setAuf((v) => !v)}>
        <span className="ki-zeile-zeit">{zeit(z.created_at)}</span>
        <span className="ki-zeile-art">{KI_ART[z.art] || z.art}</span>
        <span className="ki-zeile-bezug">{z.bezug || z.bezug_id}</span>
        <span className="ki-zeile-kurz">{z.kurz || (z.status === "fehler" ? "Fehler" : z.status === "offen" || z.status === "in_arbeit" ? "rechnet …" : "")}</span>
        <span className="ki-zeile-marken">
          {entscheidungen.length
            ? entscheidungen.map((a) => <span key={a} className={"ki-marke is-" + a}>{KI_EREIGNIS[a] || a}</span>)
            : <span className="ki-marke is-leer">ohne Entscheidung</span>}
          {z.rechenzeit_ms != null && <span className="ki-zeile-dauer">{kiDauer(z.rechenzeit_ms)}</span>}
        </span>
      </button>
      {auf && (
        <div className="ki-zeile-inhalt">
          {z.art.startsWith("clara") && z.bezug_id && !z.bezug_id.includes(":") && (
            <Link className="vg-post-link" to={`/projekte/${z.bezug_id}`}>zum Projekt</Link>
          )}
          <KiHerkunftInhalt auftragId={z.id} />
        </div>
      )}
    </li>
  );
}

export default function KiSpurPage() {
  const [art, setArt] = useState("");
  const [entscheidung, setEntscheidung] = useState("");
  const stand = useKiStand().data;
  const quoten = useKiQuoten(90).data;
  const liste = useKiListe({ art, entscheidung, limit: 40 });
  const zeilen = liste.data?.pages.flatMap((p) => p.auftraege) || [];

  return (
    <div className="projekte-page ki-seite">
      <div className="vg-page-head">
        <div className="vg-page-title">
          <Link className="vg-btn vg-btn-ghost" to="/einstellungen" aria-label="Zurück zu Einstellungen">←</Link>
          <h1>Claras Protokoll</h1>
        </div>
      </div>
      <p className="vg-detail-sub">Was Clara vorschlägt — woraus sie es gelesen hat, womit sie gerechnet hat, und was daraus wurde.</p>

      {stand && (
        <div className={"ki-stand" + (stand.evo_wach ? " ist-wach" : " ist-still")}>
          <span className="ki-stand-punkt" aria-hidden="true" />
          <span>
            {stand.evo_wach ? "Clara ist da" : "Clara ist gerade nicht erreichbar — ihre Vorschläge kommen später, Kontor läuft weiter"}
            {stand.zuletzt && <small> · zuletzt {zeit(stand.zuletzt)}</small>}
          </span>
          {stand.pausiert.length > 0 && <span className="ki-marke is-warn">angehalten: {stand.pausiert.join(", ")}</span>}
        </div>
      )}

      {quoten && quoten.arten.length > 0 && (
        <section className="ki-quoten" aria-label="Quoten der letzten 90 Tage">
          <h2 className="ki-abschnitt">Letzte {quoten.tage} Tage</h2>
          <div className="ki-quoten-raster">
            {quoten.arten.map((q) => (
              <div key={q.art} className="ki-quote">
                <div className="ki-quote-kopf"><strong>{KI_ART[q.art] || q.art}</strong><span>{q.vorschlaege} Vorschläge</span></div>
                <dl>
                  {q.art.startsWith("beleg")
                    ? <div><dt>angewandt</dt><dd>{prozent(q.angewandt, q.vorschlaege)}</dd></div>
                    : q.art === "clara_antwort"
                      ? <><div><dt>gesendet</dt><dd>{prozent(q.gesendet, q.vorschlaege)}</dd></div>
                          <div><dt>verworfen</dt><dd>{prozent(q.verworfen, q.vorschlaege)}</dd></div></>
                      : <><div><dt>übernommen</dt><dd>{prozent(q.uebernommen, q.vorschlaege)}</dd></div>
                          <div><dt>geändert</dt><dd>{prozent(q.geaendert, q.vorschlaege)}</dd></div>
                          <div><dt>verworfen</dt><dd>{prozent(q.verworfen, q.vorschlaege)}</dd></div></>}
                  <div><dt>ohne Entscheidung</dt><dd>{prozent(q.ohne_entscheidung, q.vorschlaege)}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-label="Vorschläge">
        <h2 className="ki-abschnitt">Vorschläge</h2>
        <div className="ki-filter" role="group" aria-label="Art">
          {ARTEN.map((a) => (
            <button key={a.id || "alle"} type="button" className={"ki-filter-knopf" + (art === a.id ? " ist-an" : "")} aria-pressed={art === a.id} onClick={() => setArt(a.id)}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="ki-filter" role="group" aria-label="Entscheidung">
          {ENTSCHEIDUNGEN.map((e) => (
            <button key={e.id || "jede"} type="button" className={"ki-filter-knopf is-klein" + (entscheidung === e.id ? " ist-an" : "")} aria-pressed={entscheidung === e.id} onClick={() => setEntscheidung(e.id)}>
              {e.label}
            </button>
          ))}
        </div>
        {liste.error && <p className="vg-post-fehler">{fehlerText(liste.error)}</p>}
        {liste.isLoading && <p className="ki-leer">Lädt …</p>}
        {!liste.isLoading && zeilen.length === 0 && <p className="ki-leer">Keine Vorschläge für diese Auswahl.</p>}
        <ul className="ki-liste">
          {zeilen.map((z) => <Zeile key={z.id} z={z} />)}
        </ul>
        {liste.hasNextPage && (
          <button type="button" className="vg-btn ki-mehr" disabled={liste.isFetchingNextPage} onClick={() => liste.fetchNextPage()}>
            {liste.isFetchingNextPage ? "Lädt …" : "Ältere laden"}
          </button>
        )}
        <p className="ki-fuss">Claras Gedankengang wird nach 90 Tagen geleert; alles andere bleibt.</p>
      </section>
    </div>
  );
}
